import React, { useEffect, useState, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import {
  hasRoles,
  mergeRoles,
  CREATE_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES,
} from '../../../../util/RoleUtil';
import { DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';

import { viewStaffForStudy } from '../../../../services/PersonService';
import { retrieveStudyDropdownsSpecific } from '../../../../services/StudyService';
import { viewDropdownParticipants } from '../../../../services/ParticipantService';
import { createReview } from '../../../../services/ReviewService';
import { viewDropdownVisits } from '../../../../services/VisitService';

import { formatDateAsString } from '../../../../util/StringUtil';

import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './ParticipantReviewCreate.css';

const roles = mergeRoles(CREATE_PARTICIPANT_REVIEW_ROLES, EDIT_PARTICIPANT_REVIEW_ROLES);

export const ParticipantReviewGeneralTab = ({
  context,
  setContext,
  review,
  navigateTo,
  navigateToWith,
  toolTipsEnabled,
  setSpinnerText,
  showToaster,
  editMode,
  hasQueries,
  handleUpdate,
}) => {
  const [reviewDate, setReviewDate] = useState(new Date());
  const [reviewedBy, setReviewedBy] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [study, setStudy] = useState(null);
  const [reviewLinkedToGeneral, setReviewLinkedToGeneral] = useState(true);
  const [reviewLinkedToVisitCode, setReviewLinkedToVisitCode] = useState(false);
  const [visitCode, setVisitCode] = useState(null);
  const [reviewType, setReviewType] = useState(null);
  const [noQueries, setNoQueries] = useState(false);
  const [comments, setComments] = useState('');
  const [reviewedByList, setReviewedByList] = useState([]);
  const [participantIdList, setParticipantIdList] = useState([]);
  const [studiesList, setStudiesList] = useState([]);
  const [visitCodeList, setVisitCodeList] = useState([]);
  const [studyError, setStudyError] = useState('');
  const [participantError, setParticipantError] = useState('');
  const [visitError, setVisitError] = useState('');
  const [reviewDateError, setReviewDateError] = useState('');
  const [reviewedByError, setReviewedByError] = useState('');
  const [reviewTypeError, setReviewTypeError] = useState('');
  const [disableQueryToggle, setDisableQueryToggle] = useState(false);

  const reviewTypeList = useRef(['Independent Review', 'Monitoring', 'QA Review', 'QC Review', 'QC Report', 'SDV']);

  const retrieveStudies = useCallback(async () => {
    setSpinnerText('Retrieving Studies');
    const [studyData, studyError] = await retrieveStudyDropdownsSpecific(context.username, true);
    if (studyData) {
      if (studyData.responseStatus === 'Success') {
        const formattedStudies = studyData.dropdownStudyVOs.map((study) => ({
          value: study,
          label: `${study.name}`,
        }));
        setStudiesList(formattedStudies);
      } else {
        showToaster(studyData);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: studyError.message,
      });
    }
  }, [context.username, setSpinnerText, showToaster]);

  const retrieveDropdownUsersForStudy = useCallback(
    async (locationExternalReference, participantExternalReference, studyExternalReference) => {
      const viewRoleInStudyPersonRequest = {
        locationExternalReference: locationExternalReference,
        participantExternalReference: participantExternalReference,
        studyExternalReference: studyExternalReference,
      };

      setSpinnerText('Retrieving Users');
      const [data, error] = await viewStaffForStudy(context.username, viewRoleInStudyPersonRequest);
      if (data) {
        if (data.responseStatus === 'Success') {
          const formattedUsers = data.viewAllPersonSystemUserVOs.map((user) => ({
            value: user,
            label: user.name,
          }));
          setReviewedByList(formattedUsers);

          if (!editMode) {
            const loggedInUserDropdownItem = formattedUsers.find(
              (item) => item.value.systemUserExternalReference === context.loggedInUser.externalReference
            );

            if (loggedInUserDropdownItem) {
              setReviewedBy(loggedInUserDropdownItem);
            }
          }
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    },
    [context.username, setSpinnerText, showToaster, editMode, context.loggedInUser.externalReference]
  );

  const retrieveVisitCodesDropdown = useCallback(
    async (participantExternalReference) => {
      setSpinnerText('Retrieving Visit Codes');
      const [data, error] = await viewDropdownVisits(context.username, participantExternalReference);
      if (data) {
        if (data.responseStatus === 'Success') {
          const formattedVisits = data.dropdownVisitVOS.map((visit) => ({
            value: visit,
            label: visit.visitCode,
          }));
          setVisitCodeList(formattedVisits);
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
    },
    [context.username, setSpinnerText, showToaster]
  );

  useEffect(() => {
    let participantExternalReference;

    if (review && editMode) {
      if (hasQueries) {
        setDisableQueryToggle(true);
      }
      const studyExternalReferences = {
        locationExternalReference: review?.locationExternalReference,
        studyExternalReference: review?.studyExternalReference,
      };
      setReviewDate(review.dateOfReview ? new Date(review.dateOfReview) : null);
      setStudy({
        value: studyExternalReferences,
        label: review?.study,
      });
      setReviewedBy({
        value: { name: review?.reviewedBy, externalReference: review?.reviewedByExternalReference },
        label: review?.reviewedBy,
      });

      participantExternalReference = review?.participantExternalReference;
      setParticipantId({
        value: participantExternalReference,
        label: review?.participantID,
      });

      setReviewType({
        value: review?.typeOfReview,
        label: review?.typeOfReview,
      });

      if (review.visitCode) {
        setReviewLinkedToVisitCode(true);
        setReviewLinkedToGeneral(false);
        setVisitCode({
          value: review?.visitExternalReference,
          label: review?.visitCode,
        });
      } else {
        setReviewLinkedToVisitCode(false);
        setReviewLinkedToGeneral(true);
      }
      setNoQueries(review?.noQueries || false);
      setComments(review?.comments || '');
    } else if (!editMode && context.breadcrumbs.participantReviewCreateGeneral) {
      setReviewDate(
        context.breadcrumbs.participantReviewCreateGeneral?.data?.dateOfReview
          ? new Date(context.breadcrumbs.participantReviewCreateGeneral?.data?.dateOfReview)
          : null
      );
      setStudy({
        value: context.breadcrumbs.participantReviewCreateGeneral?.data?.study?.studyExternalReference,
        label: context.breadcrumbs.participantReviewCreateGeneral?.data?.study,
      });
      setReviewedBy({
        value: context.breadcrumbs.participantReviewCreateGeneral?.data?.reviewedByExternalReference,
        label: context.breadcrumbs.participantReviewCreateGeneral?.data?.reviewedBy,
      });

      participantExternalReference =
        context.breadcrumbs.participantReviewCreateGeneral?.data?.participantExternalReference;
      setParticipantId({
        value: participantExternalReference,
        label: context.breadcrumbs.participantReviewCreateGeneral?.data?.participant,
      });

      setReviewType({
        value: context.breadcrumbs.participantReviewCreateGeneral?.data?.typeOfReview,
        label: context.breadcrumbs.participantReviewCreateGeneral?.data?.typeOfReview,
      });

      setVisitCode({
        value: context.breadcrumbs.participantReviewCreateGeneral?.data?.visitExternalReference,
        label: context.breadcrumbs.participantReviewCreateGeneral?.data?.visit,
      });

      setReviewLinkedToVisitCode(context.breadcrumbs.participantReviewCreateGeneral?.data?.reviewLinkedToVisitCode);
      setReviewLinkedToGeneral(context.breadcrumbs.participantReviewCreateGeneral?.data?.reviewLinkedToGeneral);
      setNoQueries(context.breadcrumbs.participantReviewCreateGeneral?.data?.noQueries || false);
      setComments(context.breadcrumbs.participantReviewCreateGeneral?.data?.comments || '');
    }

    retrieveStudies().then(() => setSpinnerText(null));
    if (editMode && (review.studyExternalReference || review.participantExternalReference)) {
      retrieveDropdownUsersForStudy(
        review?.locationExternalReference,
        review?.participantExternalReference,
        review?.studyExternalReference
      ).then(() => setSpinnerText(null));
    }
    retrieveVisitCodesDropdown(participantExternalReference).then(() => setSpinnerText(null));
  }, [
    context.breadcrumbs,
    editMode,
    hasQueries,
    retrieveDropdownUsersForStudy,
    retrieveStudies,
    review,
    review.studyExternalReference,
    review.participantExternalReference,
    setSpinnerText,
    retrieveVisitCodesDropdown,
  ]);

  const retrieveParticipantDropdown = async () => {
    const participantsForStudy = {
      locationExternalReference: study?.value?.locationExternalReference,
      studyExternalReference: study?.value?.studyExternalReference,
    };
    setSpinnerText('Retrieving Participants');
    const [data, error] = await viewDropdownParticipants(context.username, participantsForStudy);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedUsers = data.dropdownParticipantVOS.map((user) => ({
          value: user,
          label: user.participantID,
        }));
        setParticipantIdList(formattedUsers);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
  };

  const handleStudySelection = (selectedStudy) => {
    setStudy(selectedStudy);
    retrieveDropdownUsersForStudy(
      selectedStudy?.value?.locationExternalReference,
      null,
      selectedStudy?.value?.studyExternalReference
    ).then(() => setSpinnerText(null));
    if (selectedStudy) {
      setStudyError('');
      setParticipantError('');
    }
  };

  const handleParticipantDropdownClick = () => {
    if (!study) {
      setStudyError('Please first select a study.');
      setParticipantError('Please first select a study.');
      return;
    } else {
      setStudyError('');
      setParticipantError('');
    }
    retrieveParticipantDropdown().then(() => setSpinnerText(null));
  };

  const handleParticipantSelection = (selectedParticipant) => {
    setParticipantId(selectedParticipant);
    if (selectedParticipant) {
      setParticipantError('');
      setVisitError('');
    }

    let participantExtRef = participantId?.value?.participantExternalReference || participantId?.value;

    if (selectedParticipant && editMode && reviewLinkedToVisitCode) {
      retrieveVisitCodesDropdown(participantExtRef).then(() => setSpinnerText(null));
      setVisitCode(null);
      setVisitError('Please update visit code selection.');
    }
  };

  const handleReviewLinkedToVisitCodeChange = () => {
    if (!participantId) {
      setParticipantError('Please first select a participant.');
      setVisitError('Please first select a participant.');
      return;
    } else {
      setParticipantError('');
      setVisitError('');
    }
    setReviewLinkedToVisitCode(true);
    setReviewLinkedToGeneral(false);

    const participantExternalReference = participantId?.value?.participantExternalReference || participantId?.value;

    retrieveVisitCodesDropdown(participantExternalReference).then(() => setSpinnerText(null));
  };

  const handleVisitCodeChange = (selectedVisit) => {
    setVisitCode(selectedVisit);
    if (selectedVisit) {
      setVisitError('');
    }
  };

  const handleReviewLinkedToGeneralChange = () => {
    setReviewLinkedToGeneral(true);
    setReviewLinkedToVisitCode(false);
    setVisitCode(null);
    setVisitError('');
  };

  const handleReviewedByChange = (selectedUser) => {
    setReviewedBy(selectedUser);
  };

  const validateFields = () => {
    const currentDate = new Date();
    let isValid = true;

    if (!reviewDate) {
      setReviewDateError('*REQUIRED');
      isValid = false;
    } else if (reviewDate > currentDate) {
      setReviewDateError('Review Date cannot be future dated.');
      isValid = false;
    } else {
      setReviewDateError('');
    }

    if (!reviewedBy || reviewedBy?.label === null) {
      setReviewedByError('*REQUIRED');
      isValid = false;
    } else {
      setReviewedByError('');
    }

    if (!study || study?.label === null) {
      setStudyError('*REQUIRED');
      isValid = false;
    } else {
      setStudyError('');
    }

    if (!participantId || participantId?.label === null) {
      setParticipantError('*REQUIRED');
      isValid = false;
    } else {
      setParticipantError('');
    }

    if (!reviewType || reviewType?.label === null) {
      setReviewTypeError('*REQUIRED');
      isValid = false;
    } else {
      setReviewTypeError('');
    }

    if (reviewLinkedToVisitCode && (!visitCode || visitCode?.label === null)) {
      setVisitError('*REQUIRED');
      isValid = false;
    } else {
      setVisitError('');
    }

    return isValid;
  };

  const handleSaveChangesClick = () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }
    let visitToUpdate;
    if (!reviewLinkedToVisitCode) {
      visitToUpdate = null;
    } else {
      visitToUpdate = visitCode?.value?.visitExternalReference || visitCode?.visitExternalReference || visitCode?.value;
    }
    const newReview = {
      ...review,
      dateOfReview: formatDateAsString(reviewDate),
      reviewedBy: reviewedBy?.value?.externalReference,
      participant: participantId?.value?.participantExternalReference || participantId?.value,
      visit: visitToUpdate,
      typeOfReview: reviewType?.value,
      noQueries: noQueries,
      comments: comments,
    };
    handleUpdate(newReview);
  };

  const handleSaveAndNextClick = () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }

    const reviewToCreate = {
      dateOfReview: formatDateAsString(reviewDate),
      reviewedBy: reviewedBy?.value?.externalReference,
      participant: participantId?.value?.participantExternalReference,
      visit: visitCode?.value?.visitExternalReference,
      typeOfReview: reviewType?.value,
      noQueries: noQueries,
      comments: comments,
    };

    if (noQueries) {
      saveNewReview(reviewToCreate).then(() => navigateTo('participantReviewsTable'));
    } else {
      saveNewReview(reviewToCreate);
    }
  };

  const saveNewReview = async (review) => {
    const [data, error] = await createReview(context.username, review);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (!noQueries) {
          await navigateToWith('participantReviewsEdit', 'participantReviewCreateQuery', data.externalReference);
        }
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Review Created',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        participantReviewCreateGeneral: undefined,
      },
    }));
    navigateTo('participantReviewsTable');
  };

  const fieldsDisabled = !hasRoles(context, roles);

  return (
    <div className="participant-review-general-tab tab-container">
      <div className="participant-review-general-section">
        <div className="input-container">
          <label className={'input-label'}>Date of Review:</label>
          <DatePicker
            selected={reviewDate}
            className={`text-input ${reviewDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            disabled={fieldsDisabled}
            dropdownMode="select"
            placeholderText="Select..."
            onChange={(selectedDate) => setReviewDate(selectedDate)}
            popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Date of Review:'}
            />
          )}
        </div>
        <div className="error-message-review">
          {reviewDateError && <div className="small-red-text">{reviewDateError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Study:</label>
          <Select
            options={studiesList}
            closeMenuOnSelect={true}
            onChange={handleStudySelection}
            isDisabled={fieldsDisabled}
            value={study || null}
            classNamePrefix={`input-select${studyError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewStudy123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study:'}
            />
          )}
        </div>
        <div className="error-message-review">{studyError && <div className="small-red-text">{studyError}</div>}</div>
        <div className="input-container">
          <label className={'input-label'}>Reviewed By:</label>
          <Select
            options={reviewedByList}
            closeMenuOnSelect={true}
            onChange={handleReviewedByChange}
            value={reviewedBy || null}
            isDisabled={fieldsDisabled}
            filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
            classNamePrefix={`input-select${reviewedByError && 'error-border'}`}
            getOptionValue={(option) => option.value.externalReference}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewedBy123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Reviewed By:'}
            />
          )}
        </div>
        <div className="error-message-review">
          {reviewedByError && <div className="small-red-text">{reviewedByError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Participant ID:</label>
          <Select
            options={participantIdList}
            closeMenuOnSelect={true}
            onMenuOpen={handleParticipantDropdownClick}
            onChange={handleParticipantSelection}
            value={participantId || null}
            isDisabled={fieldsDisabled}
            classNamePrefix={`input-select${participantError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewParticipantId123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Participant ID:'}
            />
          )}
        </div>
        <div className="error-message-review">
          {participantError && <div className="small-red-text">{participantError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Review linked to:</label>
          <Checkbox
            label={'General'}
            fillStyle={true}
            onChange={handleReviewLinkedToGeneralChange}
            checked={reviewLinkedToGeneral}
            disabled={fieldsDisabled}
            color={'#000000'}
          />
          <Checkbox
            label={'Visit Code'}
            error={visitError}
            fillStyle={true}
            onChange={handleReviewLinkedToVisitCodeChange}
            checked={reviewLinkedToVisitCode}
            disabled={fieldsDisabled}
            color={'#000000'}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewLinkedTo123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Review linked to:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Visit Code:</label>
          <Select
            options={visitCodeList}
            isDisabled={!reviewLinkedToVisitCode || fieldsDisabled}
            closeMenuOnSelect={true}
            onChange={handleVisitCodeChange}
            value={visitCode || null}
            classNamePrefix={`input-select${visitError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewVisitCode123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Visit Code:'}
            />
          )}
        </div>
        <div className="error-message-review">{visitError && <div className="small-red-text">{visitError}</div>}</div>
        <div className="input-container">
          <label className={'input-label'}>Type of Review:</label>
          <Select
            options={reviewTypeList.current.map((type) => ({
              value: type,
              label: type,
            }))}
            closeMenuOnSelect={true}
            onChange={setReviewType}
            value={reviewType || null}
            isDisabled={fieldsDisabled}
            classNamePrefix={`input-select${reviewTypeError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewType123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Type of Review:'}
            />
          )}
        </div>
        <div className="error-message-review">
          {reviewTypeError && <div className="small-red-text">{reviewTypeError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>No Queries:</label>
          <Checkbox
            fillStyle={false}
            onChange={setNoQueries}
            checked={noQueries}
            color={'#000000'}
            disabled={disableQueryToggle || fieldsDisabled}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewNoQueries123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'No Queries:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-area-label'}>Comments:</label>
          <textarea
            className="input-area"
            spellCheck={true}
            type="text"
            value={comments}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setComments(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'participantReviewComments123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Comments:'}
            />
          )}
        </div>
      </div>

      <div className={'review-button-container'}>
        {editMode && (
          <>
            <SaveButton
              textColor={'#077187'}
              border={'1px solid #077187'}
              title={'Cancel'}
              onClick={handleCancelClick}
            />
            <SaveButton
              color={'#077187'}
              title={'Save Changes'}
              border={'1px solid #077187'}
              onClick={handleSaveChangesClick}
              disabled={fieldsDisabled}
            />
          </>
        )}
        {!editMode && (
          <>
            <SaveButton
              textColor={'#077187'}
              border={'1px solid #077187'}
              title={'Cancel'}
              onClick={handleCancelClick}
            />
            <SaveButton
              color={'#077187'}
              border={'1px solid #077187'}
              title={noQueries ? 'Save and Close' : 'Save and Next'}
              onClick={handleSaveAndNextClick}
            />
          </>
        )}
      </div>
    </div>
  );
};
